/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Main Application Component
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/App.js
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Authenticated routes
import Dashboard from './pages/Dashboard';
// import VulnerableApps from './pages/VulnerableApps';
import Courses from './pages/Courses';
import CourseDetails from './pages/CourseDetail';
import MyLearning from './pages/MyLearning';
import Achievements from './pages/Achievements';
import Leaderboard from './pages/Leaderboard';
import UserProfile from './pages/UserProfile';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import ModulesList from './pages/ModulesList';
import ContentPage from './pages/ContentPage';
import QuizPage from './pages/QuizPage';
import FinalQuizPage from './pages/FinalQuizPage';
import WorkshopLeaderboard from './pages/WorkshopLeaderboard';
import CourseLeaderboard from './pages/CourseLeaderboard';
import VMDashboard from './pages/VMDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure';
// Public routes
import Login from './pages/Login';
import Register from './pages/Register';
import AccountActivation from './pages/AccountActivation';
import PasswordReset from './pages/PasswordReset';
import ChangePassword from './pages/ChangePassword';
import BadgeVerificationPage from './pages/verify/badge/[id]';
import ResetPassword from './pages/ResetPassword';
function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>Initializing secure connection...</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/activate/:uidb64/:token" element={<AccountActivation />} />
        <Route path="/forgot-password" element={<PasswordReset />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route
          path="/verify/:verificationId"
          element={
            <BadgeVerificationPage />
          }
        />
      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vms"
        element={
          <ProtectedRoute>
            <VMDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id"
        element={
          <ProtectedRoute>
            <CourseDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/modules"
        element={
          <ProtectedRoute>
            <ModulesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning"
        element={
          <ProtectedRoute>
            <MyLearning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute>
            <Achievements />
          </ProtectedRoute>
        }
      />
      <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
        path="/leaderboard"
        element={
          <Leaderboard />
        }
      />
      <Route path="/leaderboard/:userId" element={<UserProfile />} />
      <Route
        path="/leaderboard/workshop/:workshopCode"
        element={
          <ProtectedRoute>
            <WorkshopLeaderboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard/course/:courseId"
        element={
          <ProtectedRoute>
            <CourseLeaderboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-failure"
        element={
          <ProtectedRoute>
            <PaymentFailure />
          </ProtectedRoute>
        }
      />
      <Route path='/learning/:courseId' element={<ModulesList />} />
      <Route path='/learning/:courseId/final-quiz' element={<FinalQuizPage />} />
      <Route path='/learning/:courseId/:contentId' element={<ContentPage />} />
      <Route path="/learning/:courseId/:moduleId/quiz" element={<QuizPage />} />

      {/* Fallback redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    
    {/* Toast notifications */}
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: '#1a1a1a',
          color: '#fff',
          border: '1px solid #333',
        },
        success: {
          style: {
            background: '#059669',
          },
        },
        error: {
          style: {
            background: '#dc2626',
          },
        },
      }}
    />
    </>
  );
}

export default App;